import { colors } from '@globals/themes/defaultTheme'
import { TenantMemberOrTeamDisplayData } from '@kudos/http-client'
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import { SxProps } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import ResponsiveScrollingTiledList from '@shared/components/ResponsiveScrollingTiledList'
import {
  getRecipientAvatar,
  getRecipientChipLabel,
} from '@shared/forms/tenantMemberAndTeamSearchField/recipientDisplayUtils'
import { T, useT } from '@transifex/react'
import useMobileRecognitionDrawer, {
  MobileRecognitionDrawerView,
} from '../useMobileRecognitionDrawer'

const paperStyles: SxProps = {
  px: 3,
  py: 1,
  borderBottomColor: colors.grayExtraLight,
  backgroundColor: colors.white,
  my: -2,
  mx: -3,
}

/** Constrains the chips list so only this row scrolls horizontally; prevents the drawer from scrolling. */
const chipsScrollWrapperStyles: SxProps = {
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
}

const displayChipStyle: SxProps = {
  m: 0.25,
  '&.MuiChip-root': {
    minHeight: 32,
  },
  backgroundColor: colors.grayUltraLight,
  borderColor: 'transparent',
}

const chipLabelStyles: SxProps = {
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
}

type MobileRecognitionRecipientsProps = {
  recipients: TenantMemberOrTeamDisplayData[]
}

const MobileRecognitionRecipients: React.FC<MobileRecognitionRecipientsProps> = ({
  recipients,
}) => {
  const t = useT()
  const { openDrawer, setView } = useMobileRecognitionDrawer()

  return (
    <Stack
      direction="row"
      spacing={1}
      component={Box}
      sx={[
        {
          alignItems: 'center',
        },
        ...(Array.isArray(paperStyles) ? paperStyles : [paperStyles]),
      ]}>
      <Typography variant="body2" sx={{ flexShrink: 0 }}>
        <T _str="To:" />
      </Typography>
      <Box sx={chipsScrollWrapperStyles}>
        <ResponsiveScrollingTiledList spacing={0.25} scrollToLastItem ariaLabel={t('Recipients')}>
          {recipients.map(recipient => (
            <Chip
              key={recipient.id}
              variant="outlined"
              size="small"
              avatar={getRecipientAvatar(recipient)}
              label={
                <Typography variant="body1" sx={chipLabelStyles}>
                  {getRecipientChipLabel(recipient)}
                </Typography>
              }
              sx={displayChipStyle}
            />
          ))}
        </ResponsiveScrollingTiledList>
      </Box>
      <AddCircleOutlineOutlinedIcon
        aria-label={t('Edit recipients')}
        role="button"
        sx={{ flexShrink: 0, color: colors.bluePrimary, cursor: 'pointer' }}
        onClick={() => {
          openDrawer()
          setView(MobileRecognitionDrawerView.RECIPIENTS)
        }}
      />
    </Stack>
  )
}

export default MobileRecognitionRecipients
